import { Button, Col, Flex, Form, Input } from 'antd'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAccountServiceResetPasswordEndpoint } from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules, RESET_EMAIL } from 'src/app-constants'
import { useLocalStorage } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { AuthHeader, PasswordStrength } from '../components'
import { useStyles } from '../styles'

export const ResetPassword = () => {
  const [isResetSuccess, setIsResetSuccess] = useState(false)

  const { t } = useLocales()
  const { styles } = useStyles()
  const [form] = Form.useForm()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const { setValue } = useLocalStorage(RESET_EMAIL, '')

  const { mutate, isPending } = useAccountServiceResetPasswordEndpoint({
    onSuccess: () => {
      setIsResetSuccess(true)
      setValue('')
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const requestBody = {
      token: decodeURIComponent(searchParams.get('token') ?? ''),
      email: decodeURIComponent(searchParams.get('email') ?? ''),
      newPassword: values?.password,
      confirmPassword: values?.confirmPassword
    }

    mutate({ requestBody })
  }

  const onRedirect = () => {
    navigate(paths.auth.root)
  }

  const passwordWatch = Form.useWatch('password', form)

  return (
    <Flex vertical gap={32}>
      <AuthHeader
        title={t(
          `auth.${isResetSuccess ? 'reset_password_success' : 'create_new_password'}`
        )}
        description={t(
          `auth.${isResetSuccess ? 'reset_password_success_subTitle' : 'create_new_password_subtitle'}`
        )}
      />

      {!isResetSuccess ? (
        <Form layout="vertical" form={form}>
          <Flex vertical gap={16}>
            <Form.Item
              name="password"
              label={t('form.password')}
              rules={requiredRules}
              className={styles.formItem}
            >
              <Input.Password size="large" placeholder="••••••••" />
            </Form.Item>

            <Col span={24}>
              <PasswordStrength value={passwordWatch ?? ''} />
            </Col>

            <Form.Item
              name="confirmPassword"
              label={t('form.repeat_password')}
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t('form.confirm_password_error')
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(t('form.confirm_password_error'))
                    )
                  }
                })
              ]}
            >
              <Input.Password size="large" placeholder="••••••••" />
            </Form.Item>

            <Button
              type="primary"
              size="large"
              onClick={onSubmit}
              loading={isPending}
            >
              {t('auth.reset_password')}
            </Button>
          </Flex>
        </Form>
      ) : (
        <Button type="primary" size="large" onClick={onRedirect}>
          {t('auth.return_to_auth')}
        </Button>
      )}
    </Flex>
  )
}
